/**
 * 保障配置 v2 (guard-v2): the per-SKU / org×SKU tolerance-signal policy on
 * `guaranteed_guard_configs.policy`.
 *
 * One signal registry ({@link GUARD_SIGNALS}) drives the write face, the
 * request path, the fault framework and the console. Every signal runs the
 * same chain: judge → this request's disposal (`walk`) → its own trip window
 * (`trip`) → recovery by the probe profile its fault locks (`recover`).
 *
 * Stored shape (`policy` JSONB):
 *   { "v": 2,
 *     "signals": { "<kind>": { threshold_ms?, walk?, retry_after_reasoning?,
 *                              retry_after_cap_s?, chat_lane?, trip?, recover?,
 *                              global_trip?, borrow_global? } },
 *     "probe_profiles": { "short": {...}, "long_stream": {...} } }
 *
 * Read rule (unchanged from v1): anything the reader does not understand
 * (`v` ≠ 2, an unknown top-level key, an unknown signal kind, a value that is
 * not an object) reads as all-off; an illegal field inside a known section is
 * dropped (inherit); JSON null is kept as explicit off.
 *
 * Scope rules:
 *   - SKU rows: every field except `global_trip` / `borrow_global`.
 *   - org×SKU rows: thresholds, `walk`, `retry_after_reasoning`, `chat_lane`, the org's own
 *     `trip`, `global_trip` (inherit | own) and `borrow_global`. Recovery
 *     (`recover`, `probe_profiles`) and `retry_after_cap_s` are SKU-only.
 *   - Merge: an org field that is unset inherits the SKU value; a SKU field
 *     that is unset is off; an org field set to JSON null is off.
 *
 * Evidence is shared, decisions are isolated (02-design §12): a request whose
 * effective threshold or walk for a signal differs from the SKU's is
 * `censored` for that signal and never feeds the SKU-global window, so an org
 * setting can never trip a leg for anybody else.
 *
 * `first_content_stream.threshold_ms` keeps v1 `first_content.stream_ms`'s
 * relation to the legacy `guaranteed_service_entries.no_response_cutoff_ms`
 * column: see {@link resolveNoResponseCutoffMs}.
 */

export const GUARD_FIRST_CONTENT_STREAM_MS_MIN = 10_000;
export const GUARD_FIRST_CONTENT_STREAM_MS_MAX = 120_000;
export const GUARD_FIRST_CONTENT_NONSTREAM_MS_MIN = 10_000;
export const GUARD_FIRST_CONTENT_NONSTREAM_MS_MAX = 600_000;

export const GUARD_STALL_CUTOFF_MS_MIN = 15_000;
export const GUARD_STALL_CUTOFF_MS_MAX = 300_000;

export const GUARD_TRIP_MIN_HITS_MIN = 1;
export const GUARD_TRIP_MIN_HITS_MAX = 100;
export const GUARD_TRIP_WINDOW_SECONDS_MIN = 30;
export const GUARD_TRIP_WINDOW_SECONDS_MAX = 3_600;
export const GUARD_TRIP_MIN_RATIO_MIN = 0;
export const GUARD_TRIP_MIN_RATIO_MAX = 1;
export const GUARD_TRIP_MAX_HOLD_SECONDS_MIN = 60;
export const GUARD_TRIP_MAX_HOLD_SECONDS_MAX = 86_400;

export const GUARD_RECOVER_CLEAN_MIN = 1;
export const GUARD_RECOVER_CLEAN_MAX = 10;
/** Successful probe waves a fault needs when its signal sets no `recover.clean` (v1 default). */
export const GUARD_RECOVER_CLEAN_DEFAULT = 2;

export const GUARD_PROBE_INTERVAL_SECONDS_MIN = 30;
export const GUARD_PROBE_INTERVAL_SECONDS_MAX = 3_600;
export const GUARD_PROBE_SHOTS_MIN = 1;
export const GUARD_PROBE_SHOTS_MAX = 8;
export const GUARD_PROBE_SHORT_TIMEOUT_SECONDS_MIN = 5;
export const GUARD_PROBE_SHORT_TIMEOUT_SECONDS_MAX = 120;
export const GUARD_PROBE_LONG_TIMEOUT_SECONDS_MIN = 30;
export const GUARD_PROBE_LONG_TIMEOUT_SECONDS_MAX = 900;
export const GUARD_PROBE_LONG_MIN_STREAM_SECONDS_MIN = 10;
export const GUARD_PROBE_LONG_MIN_STREAM_SECONDS_MAX = 600;
export const GUARD_PROBE_LONG_MAX_GAP_SECONDS_MIN = 1;
export const GUARD_PROBE_LONG_MAX_GAP_SECONDS_MAX = 300;

/**
 * R1-B6: the `long_stream` probe's output budget (tokens) and the profile
 * defaults the console offers. A long shot is clean only after
 * `min_stream_s` of content and before `timeout_s`; the task asks for more
 * than the budget, so a shot streams `budget / rate` seconds. With 8192
 * tokens, `min_stream_s` 30 and `timeout_s` 240 that holds for an upstream
 * between ~35 and ~270 output tokens/s (8192/240 … 8192/30); outside it the
 * operator raises `timeout_s` (slow) or lowers `min_stream_s` (fast).
 */
export const GUARD_LONG_STREAM_PROBE_OUTPUT_TOKENS = 8192;
export const GUARD_LONG_STREAM_PROFILE_DEFAULT = {
  interval_s: 600,
  shots: 1,
  timeout_s: 240,
  min_stream_s: 30,
  max_gap_s: 30,
} as const;

export const GUARD_RETRY_AFTER_CAP_SECONDS_MIN = 1;
export const GUARD_RETRY_AFTER_CAP_SECONDS_MAX = 3_600;

export const GUARD_POLICY_VERSION = 2;

export const GUARD_MODES = ['off', 'shadow', 'act'] as const;
export type GuardMode = (typeof GUARD_MODES)[number];

/**
 * Recover-probe transport, decided by the leg's lane at send time
 * (`resolveCliProbeRecoverShape`). Not configurable since v2; the fault row's
 * `recover_probe_kind` keeps one of these as its grouping key.
 */
export const GUARD_PROBE_TRANSPORTS = ['codex-cli', 'claude-code-cli', 'chat-stream'] as const;
export type GuardProbeTransport = (typeof GUARD_PROBE_TRANSPORTS)[number];
/** The transport new fault rows store (v1's default). */
export const GUARD_DEFAULT_PROBE_TRANSPORT: GuardProbeTransport = 'codex-cli';

export const GUARD_PROBE_PROFILES = ['short', 'long_stream'] as const;
export type GuardProbeProfile = (typeof GUARD_PROBE_PROFILES)[number];

/**
 * Profile coverage (a partial order): an observation of profile P proves
 * every fault whose required profile is in `covers(P)`. `long_stream` meets
 * every `short` criterion, so a long success also counts for short faults.
 */
export const GUARD_PROBE_PROFILE_COVERS: Readonly<Record<GuardProbeProfile, readonly GuardProbeProfile[]>> = {
  short: ['short'],
  long_stream: ['long_stream', 'short'],
};

export function guardProfileCovers(observed: GuardProbeProfile, required: GuardProbeProfile): boolean {
  return GUARD_PROBE_PROFILE_COVERS[observed].includes(required);
}

export function isGuardProbeProfile(value: unknown): value is GuardProbeProfile {
  return value === 'short' || value === 'long_stream';
}

export const GUARD_SIGNAL_KINDS = [
  'overload',
  'first_content_stream',
  'first_content_nonstream',
  'prelude_fail',
  'stall',
  'broken',
] as const;
export type GuardSignalKind = (typeof GUARD_SIGNAL_KINDS)[number];

export const GUARD_GLOBAL_TRIP_CHOICES = ['inherit', 'own'] as const;
export type GuardGlobalTripChoice = (typeof GUARD_GLOBAL_TRIP_CHOICES)[number];

/** One registered tolerance signal (02-design §2). */
export interface GuardSignalSpec {
  readonly kind: GuardSignalKind;
  /** `routing_leg_faults.fault_kind`. S5 / S6 keep their v1 names. */
  readonly faultKind: string;
  /** Tair prefix of the SKU-global trip window. S5 / S6 keep v1's prefixes. */
  readonly windowPrefix: string;
  /** Threshold range in ms; null = the signal is judged on sight. */
  readonly threshold: { readonly min: number; readonly max: number } | null;
  /** `none`: nothing can be done for this request (S6: content already sent). */
  readonly walk: 'modes' | 'none';
  /** A walk this signal takes is not a fault (no breaker failure, no chain / Jev count). */
  readonly walkNoFault: boolean;
  readonly protocols: 'all' | 'responses';
  readonly retryAfterReasoning: boolean;
  readonly retryAfterCap: boolean;
  /**
   * `chat_lane` (S4 only, 2026-11-02 client3398): an act walk also arms on the
   * chat / completions tenant lane, where the Responses -> chat codec hides the
   * prelude and renders `response.failed` as a bare `[DONE]`. Explicit opt-in:
   * an unset field (every 0146-backfilled row) leaves the lane off.
   */
  readonly chatLane: boolean;
  /** `recover.profile` when the section has no `recover` (console and runtime alike). */
  readonly defaultRecoverProfile: GuardProbeProfile;
  /** `recover.clean` when the section has no `recover` (console and runtime alike). */
  readonly defaultRecoverClean: number;
}

export const GUARD_SIGNALS: readonly GuardSignalSpec[] = [
  {
    kind: 'overload',
    faultKind: 'overload',
    windowPrefix: 'trip:overload',
    threshold: null,
    walk: 'modes',
    walkNoFault: true,
    protocols: 'all',
    retryAfterReasoning: false,
    retryAfterCap: true,
    chatLane: false,
    defaultRecoverProfile: 'short',
    defaultRecoverClean: GUARD_RECOVER_CLEAN_DEFAULT,
  },
  {
    kind: 'first_content_stream',
    faultKind: 'first_content_stream',
    windowPrefix: 'trip:first_content_stream',
    threshold: { min: GUARD_FIRST_CONTENT_STREAM_MS_MIN, max: GUARD_FIRST_CONTENT_STREAM_MS_MAX },
    walk: 'modes',
    walkNoFault: false,
    protocols: 'all',
    retryAfterReasoning: false,
    retryAfterCap: false,
    chatLane: false,
    defaultRecoverProfile: 'short',
    defaultRecoverClean: GUARD_RECOVER_CLEAN_DEFAULT,
  },
  {
    kind: 'first_content_nonstream',
    faultKind: 'first_content_nonstream',
    windowPrefix: 'trip:first_content_nonstream',
    threshold: { min: GUARD_FIRST_CONTENT_NONSTREAM_MS_MIN, max: GUARD_FIRST_CONTENT_NONSTREAM_MS_MAX },
    walk: 'modes',
    walkNoFault: false,
    protocols: 'all',
    retryAfterReasoning: false,
    retryAfterCap: false,
    chatLane: false,
    defaultRecoverProfile: 'short',
    defaultRecoverClean: GUARD_RECOVER_CLEAN_DEFAULT,
  },
  {
    kind: 'prelude_fail',
    faultKind: 'prelude_fail',
    windowPrefix: 'trip:prelude_fail',
    threshold: null,
    walk: 'modes',
    walkNoFault: false,
    protocols: 'responses',
    retryAfterReasoning: false,
    retryAfterCap: false,
    chatLane: true,
    defaultRecoverProfile: 'short',
    defaultRecoverClean: GUARD_RECOVER_CLEAN_DEFAULT,
  },
  {
    kind: 'stall',
    faultKind: 'silent',
    windowPrefix: 'silenttrip',
    threshold: { min: GUARD_STALL_CUTOFF_MS_MIN, max: GUARD_STALL_CUTOFF_MS_MAX },
    walk: 'modes',
    walkNoFault: false,
    protocols: 'all',
    retryAfterReasoning: true,
    retryAfterCap: false,
    chatLane: false,
    defaultRecoverProfile: 'short',
    defaultRecoverClean: GUARD_RECOVER_CLEAN_DEFAULT,
  },
  {
    kind: 'broken',
    faultKind: 'broken',
    windowPrefix: 'brokentrip',
    threshold: null,
    walk: 'none',
    walkNoFault: false,
    protocols: 'all',
    retryAfterReasoning: false,
    retryAfterCap: false,
    chatLane: false,
    defaultRecoverProfile: 'long_stream',
    defaultRecoverClean: 3,
  },
];

const SIGNAL_BY_KIND = new Map<string, GuardSignalSpec>(GUARD_SIGNALS.map((spec) => [spec.kind, spec]));
const SIGNAL_BY_FAULT_KIND = new Map<string, GuardSignalSpec>(GUARD_SIGNALS.map((spec) => [spec.faultKind, spec]));

export function guardSignalSpec(kind: GuardSignalKind): GuardSignalSpec {
  const spec = SIGNAL_BY_KIND.get(kind);
  if (spec === undefined) throw new Error(`unregistered guard signal ${kind}`);
  return spec;
}

/** The signal a `routing_leg_faults.fault_kind` belongs to (`silent` → `stall`). */
export function guardSignalOfFaultKind(faultKind: string): GuardSignalSpec | undefined {
  return SIGNAL_BY_FAULT_KIND.get(faultKind);
}

export function isGuardSignalKind(value: unknown): value is GuardSignalKind {
  return typeof value === 'string' && SIGNAL_BY_KIND.has(value);
}

export function isGuardMode(value: unknown): value is GuardMode {
  return value === 'off' || value === 'shadow' || value === 'act';
}

/**
 * A trip rule. Armed (`shadow` / `act`) needs the whole rule; `off` may keep
 * the last numbers (the page remembers them) or carry none.
 */
export type GuardTripSpec =
  | {
    readonly mode: 'off';
    readonly window_s?: number;
    readonly min_hits?: number;
    readonly min_ratio?: number;
    readonly max_hold_s?: number;
  }
  | {
    readonly mode: 'shadow' | 'act';
    readonly window_s: number;
    readonly min_hits: number;
    readonly min_ratio: number;
    readonly max_hold_s: number;
  };

export interface GuardRecoverSpec {
  readonly profile: GuardProbeProfile;
  readonly clean: number;
}

/** One signal's stored section. JSON null on an org field is explicit off. */
export interface GuardSignalSection {
  readonly threshold_ms?: number | null;
  readonly walk?: GuardMode | null;
  readonly retry_after_reasoning?: boolean | null;
  readonly retry_after_cap_s?: number | null;
  /** S4 only: also walk on the chat / completions lane (see {@link GuardSignalSpec.chatLane}). */
  readonly chat_lane?: boolean | null;
  readonly trip?: GuardTripSpec | null;
  readonly recover?: GuardRecoverSpec | null;
  readonly global_trip?: GuardGlobalTripChoice | null;
  readonly borrow_global?: boolean | null;
}

export interface GuardShortProfileSpec {
  readonly interval_s: number;
  readonly shots: number;
  readonly timeout_s: number;
}

export interface GuardLongStreamProfileSpec extends GuardShortProfileSpec {
  readonly min_stream_s: number;
  readonly max_gap_s: number;
}

export interface GuardProbeProfilesSection {
  readonly short?: GuardShortProfileSpec;
  readonly long_stream?: GuardLongStreamProfileSpec;
}

export type GuardSignalsSection = Readonly<Partial<Record<GuardSignalKind, GuardSignalSection>>>;

/** Stored `policy` JSONB. A missing signal is off (SKU) / inherit (org). */
export interface StoredGuardPolicy {
  readonly v: 2;
  readonly signals?: GuardSignalsSection;
  readonly probe_profiles?: GuardProbeProfilesSection;
}

export type GuardScope = 'sku' | 'org_sku';

export type GuardParseOk<T> = { readonly ok: true; readonly value: T };
export type GuardParseErr = { readonly ok: false; readonly message: string };
export type GuardParseResult<T> = GuardParseOk<T> | GuardParseErr;

export const EMPTY_GUARD_POLICY: StoredGuardPolicy = { v: 2 };

type SignalField = keyof GuardSignalSection;

const SIGNAL_FIELDS: readonly SignalField[] = [
  'threshold_ms', 'walk', 'retry_after_reasoning', 'retry_after_cap_s', 'chat_lane', 'trip', 'recover', 'global_trip',
  'borrow_global',
];
const TOP_LEVEL_KEYS = new Set(['v', 'signals', 'probe_profiles']);
const TRIP_FIELDS = new Set(['mode', 'window_s', 'min_hits', 'min_ratio', 'max_hold_s']);
const RECOVER_FIELDS = new Set(['profile', 'clean']);
const SHORT_FIELDS = new Set(['interval_s', 'shots', 'timeout_s']);
const LONG_FIELDS = new Set(['interval_s', 'shots', 'timeout_s', 'min_stream_s', 'max_gap_s']);
const UNSET_KEY = 'unset';

/** Fields one signal may carry on one scope. */
export function guardSignalFields(kind: GuardSignalKind, scope: GuardScope): readonly SignalField[] {
  const spec = guardSignalSpec(kind);
  return SIGNAL_FIELDS.filter((field) => {
    switch (field) {
      case 'threshold_ms': return spec.threshold !== null;
      case 'walk': return spec.walk !== 'none';
      case 'retry_after_reasoning': return spec.retryAfterReasoning;
      case 'retry_after_cap_s': return spec.retryAfterCap && scope === 'sku';
      case 'chat_lane': return spec.chatLane;
      case 'trip': return true;
      case 'recover': return scope === 'sku';
      case 'global_trip': return scope === 'org_sku';
      case 'borrow_global': return scope === 'org_sku';
    }
    return false;
  });
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function unknownKeys(record: Record<string, unknown>, allowed: ReadonlySet<string>): string[] {
  return Object.keys(record).filter((key) => !allowed.has(key)).sort();
}

function isIntInRange(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= max;
}

function isRatio(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
    && value >= GUARD_TRIP_MIN_RATIO_MIN && value <= GUARD_TRIP_MIN_RATIO_MAX;
}

function rangeMessage(name: string, min: number, max: number): string {
  return `${name} must be an integer from ${String(min)} to ${String(max)}`;
}

function fail(message: string): GuardParseErr {
  return { ok: false, message };
}

function parseTrip(value: unknown, name: string): GuardParseResult<GuardTripSpec> {
  if (!isPlainObject(value)) return fail(`${name} must be an object or null`);
  const extra = unknownKeys(value, TRIP_FIELDS);
  if (extra.length > 0) return fail(`${name} has unknown field(s): ${extra.join(', ')}`);
  const mode = value.mode;
  if (!isGuardMode(mode)) return fail(`${name}.mode must be 'off', 'shadow' or 'act'`);
  const off = mode === 'off';
  const present = (key: string): boolean => key in value && value[key] !== undefined;
  if (!(off && !present('window_s')) && !isIntInRange(value.window_s, GUARD_TRIP_WINDOW_SECONDS_MIN, GUARD_TRIP_WINDOW_SECONDS_MAX)) {
    return fail(rangeMessage(`${name}.window_s`, GUARD_TRIP_WINDOW_SECONDS_MIN, GUARD_TRIP_WINDOW_SECONDS_MAX));
  }
  if (!(off && !present('min_hits')) && !isIntInRange(value.min_hits, GUARD_TRIP_MIN_HITS_MIN, GUARD_TRIP_MIN_HITS_MAX)) {
    return fail(rangeMessage(`${name}.min_hits`, GUARD_TRIP_MIN_HITS_MIN, GUARD_TRIP_MIN_HITS_MAX));
  }
  if (!(off && !present('min_ratio')) && !isRatio(value.min_ratio)) {
    return fail(`${name}.min_ratio must be a number from ${String(GUARD_TRIP_MIN_RATIO_MIN)} to ${String(GUARD_TRIP_MIN_RATIO_MAX)}`);
  }
  if (!(off && !present('max_hold_s')) && !isIntInRange(value.max_hold_s, GUARD_TRIP_MAX_HOLD_SECONDS_MIN, GUARD_TRIP_MAX_HOLD_SECONDS_MAX)) {
    return fail(rangeMessage(`${name}.max_hold_s`, GUARD_TRIP_MAX_HOLD_SECONDS_MIN, GUARD_TRIP_MAX_HOLD_SECONDS_MAX));
  }
  const windowS = value.window_s;
  const minHits = value.min_hits;
  const minRatio = value.min_ratio;
  const maxHoldS = value.max_hold_s;
  if (off) {
    return {
      ok: true,
      value: {
        mode: 'off',
        ...(typeof windowS === 'number' ? { window_s: windowS } : {}),
        ...(typeof minHits === 'number' ? { min_hits: minHits } : {}),
        ...(typeof minRatio === 'number' ? { min_ratio: minRatio } : {}),
        ...(typeof maxHoldS === 'number' ? { max_hold_s: maxHoldS } : {}),
      },
    };
  }
  if (typeof windowS !== 'number' || typeof minHits !== 'number' || typeof minRatio !== 'number' || typeof maxHoldS !== 'number') {
    return fail(`${name} is incomplete`);
  }
  return { ok: true, value: { mode, window_s: windowS, min_hits: minHits, min_ratio: minRatio, max_hold_s: maxHoldS } };
}

function parseRecover(value: unknown, name: string): GuardParseResult<GuardRecoverSpec> {
  if (!isPlainObject(value)) return fail(`${name} must be an object or null`);
  const extra = unknownKeys(value, RECOVER_FIELDS);
  if (extra.length > 0) return fail(`${name} has unknown field(s): ${extra.join(', ')}`);
  if (!isGuardProbeProfile(value.profile)) return fail(`${name}.profile must be 'short' or 'long_stream'`);
  if (!isIntInRange(value.clean, GUARD_RECOVER_CLEAN_MIN, GUARD_RECOVER_CLEAN_MAX)) {
    return fail(rangeMessage(`${name}.clean`, GUARD_RECOVER_CLEAN_MIN, GUARD_RECOVER_CLEAN_MAX));
  }
  return { ok: true, value: { profile: value.profile, clean: value.clean } };
}

function parseShortProfile(value: unknown, name: string): GuardParseResult<GuardShortProfileSpec> {
  if (!isPlainObject(value)) return fail(`${name} must be an object or null`);
  const extra = unknownKeys(value, SHORT_FIELDS);
  if (extra.length > 0) return fail(`${name} has unknown field(s): ${extra.join(', ')}`);
  if (!isIntInRange(value.interval_s, GUARD_PROBE_INTERVAL_SECONDS_MIN, GUARD_PROBE_INTERVAL_SECONDS_MAX)) {
    return fail(rangeMessage(`${name}.interval_s`, GUARD_PROBE_INTERVAL_SECONDS_MIN, GUARD_PROBE_INTERVAL_SECONDS_MAX));
  }
  if (!isIntInRange(value.shots, GUARD_PROBE_SHOTS_MIN, GUARD_PROBE_SHOTS_MAX)) {
    return fail(rangeMessage(`${name}.shots`, GUARD_PROBE_SHOTS_MIN, GUARD_PROBE_SHOTS_MAX));
  }
  if (!isIntInRange(value.timeout_s, GUARD_PROBE_SHORT_TIMEOUT_SECONDS_MIN, GUARD_PROBE_SHORT_TIMEOUT_SECONDS_MAX)) {
    return fail(rangeMessage(`${name}.timeout_s`, GUARD_PROBE_SHORT_TIMEOUT_SECONDS_MIN, GUARD_PROBE_SHORT_TIMEOUT_SECONDS_MAX));
  }
  return { ok: true, value: { interval_s: value.interval_s, shots: value.shots, timeout_s: value.timeout_s } };
}

function parseLongProfile(value: unknown, name: string): GuardParseResult<GuardLongStreamProfileSpec> {
  if (!isPlainObject(value)) return fail(`${name} must be an object or null`);
  const extra = unknownKeys(value, LONG_FIELDS);
  if (extra.length > 0) return fail(`${name} has unknown field(s): ${extra.join(', ')}`);
  if (!isIntInRange(value.interval_s, GUARD_PROBE_INTERVAL_SECONDS_MIN, GUARD_PROBE_INTERVAL_SECONDS_MAX)) {
    return fail(rangeMessage(`${name}.interval_s`, GUARD_PROBE_INTERVAL_SECONDS_MIN, GUARD_PROBE_INTERVAL_SECONDS_MAX));
  }
  if (!isIntInRange(value.shots, GUARD_PROBE_SHOTS_MIN, GUARD_PROBE_SHOTS_MAX)) {
    return fail(rangeMessage(`${name}.shots`, GUARD_PROBE_SHOTS_MIN, GUARD_PROBE_SHOTS_MAX));
  }
  if (!isIntInRange(value.timeout_s, GUARD_PROBE_LONG_TIMEOUT_SECONDS_MIN, GUARD_PROBE_LONG_TIMEOUT_SECONDS_MAX)) {
    return fail(rangeMessage(`${name}.timeout_s`, GUARD_PROBE_LONG_TIMEOUT_SECONDS_MIN, GUARD_PROBE_LONG_TIMEOUT_SECONDS_MAX));
  }
  if (!isIntInRange(value.min_stream_s, GUARD_PROBE_LONG_MIN_STREAM_SECONDS_MIN, GUARD_PROBE_LONG_MIN_STREAM_SECONDS_MAX)) {
    return fail(rangeMessage(`${name}.min_stream_s`, GUARD_PROBE_LONG_MIN_STREAM_SECONDS_MIN, GUARD_PROBE_LONG_MIN_STREAM_SECONDS_MAX));
  }
  if (!isIntInRange(value.max_gap_s, GUARD_PROBE_LONG_MAX_GAP_SECONDS_MIN, GUARD_PROBE_LONG_MAX_GAP_SECONDS_MAX)) {
    return fail(rangeMessage(`${name}.max_gap_s`, GUARD_PROBE_LONG_MAX_GAP_SECONDS_MIN, GUARD_PROBE_LONG_MAX_GAP_SECONDS_MAX));
  }
  if (value.timeout_s <= value.min_stream_s) {
    return fail(`${name}.timeout_s must be greater than ${name}.min_stream_s`);
  }
  return {
    ok: true,
    value: {
      interval_s: value.interval_s,
      shots: value.shots,
      timeout_s: value.timeout_s,
      min_stream_s: value.min_stream_s,
      max_gap_s: value.max_gap_s,
    },
  };
}

/** Strict parse of one signal section on one scope (write face). */
function parseSignalSectionStrict(
  kind: GuardSignalKind,
  value: unknown,
  scope: GuardScope,
): GuardParseResult<GuardSignalSection> {
  const name = `signals.${kind}`;
  if (!isPlainObject(value)) return fail(`${name} must be an object or null`);
  const allowed = new Set<string>(guardSignalFields(kind, scope));
  const extra = unknownKeys(value, allowed);
  if (extra.length > 0) {
    const scoped = extra.filter((key) => (SIGNAL_FIELDS as readonly string[]).includes(key));
    if (scoped.length === extra.length) {
      return fail(`${name}: ${extra.join(', ')} cannot be set on ${scope === 'sku' ? 'a SKU' : 'an org×SKU'} row for this signal`);
    }
    return fail(`${name} has unknown field(s): ${extra.join(', ')}`);
  }
  const spec = guardSignalSpec(kind);
  const org = scope === 'org_sku';
  const out: {
    threshold_ms?: number | null;
    walk?: GuardMode | null;
    retry_after_reasoning?: boolean | null;
    retry_after_cap_s?: number | null;
    chat_lane?: boolean | null;
    trip?: GuardTripSpec | null;
    recover?: GuardRecoverSpec | null;
    global_trip?: GuardGlobalTripChoice | null;
    borrow_global?: boolean | null;
  } = {};
  if ('threshold_ms' in value && spec.threshold !== null) {
    const raw = value.threshold_ms;
    if (raw === null) out.threshold_ms = null;
    else if (!isIntInRange(raw, spec.threshold.min, spec.threshold.max)) {
      return fail(rangeMessage(`${name}.threshold_ms`, spec.threshold.min, spec.threshold.max));
    } else out.threshold_ms = raw;
  }
  if ('walk' in value) {
    const raw = value.walk;
    if (raw === null) out.walk = null;
    else if (!isGuardMode(raw)) return fail(`${name}.walk must be 'off', 'shadow' or 'act'`);
    else out.walk = raw;
  }
  if ('retry_after_reasoning' in value) {
    const raw = value.retry_after_reasoning;
    if (raw === null) out.retry_after_reasoning = null;
    else if (typeof raw !== 'boolean') return fail(`${name}.retry_after_reasoning must be a boolean`);
    else out.retry_after_reasoning = raw;
  }
  if ('retry_after_cap_s' in value) {
    const raw = value.retry_after_cap_s;
    if (raw === null) out.retry_after_cap_s = null;
    else if (!isIntInRange(raw, GUARD_RETRY_AFTER_CAP_SECONDS_MIN, GUARD_RETRY_AFTER_CAP_SECONDS_MAX)) {
      return fail(rangeMessage(`${name}.retry_after_cap_s`, GUARD_RETRY_AFTER_CAP_SECONDS_MIN, GUARD_RETRY_AFTER_CAP_SECONDS_MAX));
    } else out.retry_after_cap_s = raw;
  }
  if ('chat_lane' in value) {
    const raw = value.chat_lane;
    if (raw === null) out.chat_lane = null;
    else if (typeof raw !== 'boolean') return fail(`${name}.chat_lane must be a boolean`);
    else out.chat_lane = raw;
  }
  if ('trip' in value) {
    if (value.trip === null) out.trip = null;
    else {
      const parsed = parseTrip(value.trip, `${name}.trip`);
      if (!parsed.ok) return parsed;
      out.trip = parsed.value;
    }
  }
  if ('recover' in value) {
    if (value.recover === null) out.recover = null;
    else {
      const parsed = parseRecover(value.recover, `${name}.recover`);
      if (!parsed.ok) return parsed;
      out.recover = parsed.value;
    }
  }
  if ('global_trip' in value) {
    const raw = value.global_trip;
    if (raw === null) out.global_trip = null;
    else if (raw !== 'inherit' && raw !== 'own') return fail(`${name}.global_trip must be 'inherit' or 'own'`);
    else out.global_trip = raw;
  }
  if ('borrow_global' in value) {
    const raw = value.borrow_global;
    if (raw === null) out.borrow_global = null;
    else if (typeof raw !== 'boolean') return fail(`${name}.borrow_global must be a boolean`);
    else out.borrow_global = raw;
  }
  if (!org) {
    // SKU disposal completeness (v1 parity: a SKU stall section was complete).
    if (kind === 'stall' && ('walk' in out || 'threshold_ms' in out || 'retry_after_reasoning' in out)) {
      if (!isGuardMode(out.walk)) return fail(`${name}.walk must be 'off', 'shadow' or 'act'`);
      if (typeof out.threshold_ms !== 'number') {
        return fail(rangeMessage(`${name}.threshold_ms`, GUARD_STALL_CUTOFF_MS_MIN, GUARD_STALL_CUTOFF_MS_MAX));
      }
      if (typeof out.retry_after_reasoning !== 'boolean') return fail(`${name}.retry_after_reasoning must be a boolean`);
    }
    if ((kind === 'first_content_stream' || kind === 'first_content_nonstream')) {
      if (typeof out.threshold_ms === 'number' && !isGuardMode(out.walk)) {
        return fail(`${name}.walk is required when ${name}.threshold_ms is set`);
      }
      if ((out.walk === 'shadow' || out.walk === 'act') && typeof out.threshold_ms !== 'number') {
        return fail(`${name}.threshold_ms is required when ${name}.walk is '${out.walk}'`);
      }
    }
  }
  return { ok: true, value: out };
}

function parseProbeProfilesStrict(value: unknown): GuardParseResult<GuardProbeProfilesSection> {
  if (!isPlainObject(value)) return fail('probe_profiles must be an object');
  const extra = unknownKeys(value, new Set<string>(GUARD_PROBE_PROFILES));
  if (extra.length > 0) return fail(`probe_profiles has unknown profile(s): ${extra.join(', ')}`);
  const out: { short?: GuardShortProfileSpec; long_stream?: GuardLongStreamProfileSpec } = {};
  if (value.short !== undefined && value.short !== null) {
    const parsed = parseShortProfile(value.short, 'probe_profiles.short');
    if (!parsed.ok) return parsed;
    out.short = parsed.value;
  }
  if (value.long_stream !== undefined && value.long_stream !== null) {
    const parsed = parseLongProfile(value.long_stream, 'probe_profiles.long_stream');
    if (!parsed.ok) return parsed;
    out.long_stream = parsed.value;
  }
  return { ok: true, value: out };
}

/**
 * R1-B7: an armed SKU trip whose recover profile has no `probe_profiles`
 * entry could only come back at `max_hold_s` (the recover scheduler has
 * nothing to send). A save that touches that trip / recover or the probe
 * profiles is refused instead. A stored row is not re-judged on an unrelated
 * edit (a v1 `silent_trip` without `silent_recover` upgrades to exactly this
 * shape and keeps its v1 meaning).
 */
function missingRecoverProfile(
  signals: Readonly<Partial<Record<GuardSignalKind, GuardSignalSection>>>,
  profiles: GuardProbeProfilesSection | undefined,
  kinds: readonly GuardSignalKind[],
): string | null {
  for (const kind of kinds) {
    const section = signals[kind];
    const mode = section?.trip?.mode;
    if (mode !== 'shadow' && mode !== 'act') continue;
    const profile = section?.recover?.profile ?? guardSignalSpec(kind).defaultRecoverProfile;
    if (profiles?.[profile] === undefined) {
      return `signals.${kind}.trip is ${mode} but probe_profiles.${profile} is not set: `
        + 'a tripped leg could only come back when max_hold_s runs out. Set the probe profile in the same save.';
    }
  }
  return null;
}

/**
 * Strict parse of a whole stored policy for one scope. The write face runs
 * it on the merged result, so a write can never store something the reader
 * would silently drop.
 */
export function parseGuardPolicyWrite(input: unknown, scope: GuardScope): GuardParseResult<StoredGuardPolicy> {
  if (!isPlainObject(input)) return fail('policy must be an object');
  const extra = unknownKeys(input, TOP_LEVEL_KEYS);
  if (extra.length > 0) return fail(`policy has unknown field(s): ${extra.join(', ')}`);
  if (input.v !== GUARD_POLICY_VERSION) return fail('policy.v must be 2');
  const signals: Partial<Record<GuardSignalKind, GuardSignalSection>> = {};
  if (input.signals !== undefined) {
    if (!isPlainObject(input.signals)) return fail('policy.signals must be an object');
    const unknown = Object.keys(input.signals).filter((key) => !isGuardSignalKind(key)).sort();
    if (unknown.length > 0) return fail(`policy.signals has unknown signal(s): ${unknown.join(', ')}`);
    for (const kind of GUARD_SIGNAL_KINDS) {
      if (!(kind in input.signals)) continue;
      const parsed = parseSignalSectionStrict(kind, input.signals[kind], scope);
      if (!parsed.ok) return parsed;
      if (Object.keys(parsed.value).length > 0) signals[kind] = parsed.value;
    }
  }
  let probeProfiles: GuardProbeProfilesSection | undefined;
  if (input.probe_profiles !== undefined) {
    if (scope === 'org_sku') return fail('probe_profiles cannot be set on an org×SKU row; recovery is SKU-only');
    const parsed = parseProbeProfilesStrict(input.probe_profiles);
    if (!parsed.ok) return parsed;
    if (Object.keys(parsed.value).length > 0) probeProfiles = parsed.value;
  }
  return {
    ok: true,
    value: {
      v: 2,
      ...(Object.keys(signals).length > 0 ? { signals } : {}),
      ...(probeProfiles !== undefined ? { probe_profiles: probeProfiles } : {}),
    },
  };
}

/** One PUT's `policy` patch (02-design §7): sections merge field by field. */
export interface GuardPolicyPatch {
  readonly signals?: Readonly<Partial<Record<GuardSignalKind, (GuardSignalSection & { readonly unset?: readonly string[] }) | null>>>;
  readonly probe_profiles?: Readonly<Partial<Record<GuardProbeProfile, GuardShortProfileSpec | GuardLongStreamProfileSpec | null>>>;
}

/**
 * Apply a PUT patch to the stored policy and validate the result strictly.
 *
 *   signals.<kind>: null            → the signal section is removed
 *   signals.<kind>: { …fields }     → fields merge into the stored section;
 *                                      `trip` / `recover` replace whole;
 *                                      a field set to null is stored as null
 *   signals.<kind>.unset: [field]   → the field is deleted (org: inherit)
 *   probe_profiles.<p>: null | spec → removed | replaced
 */
export function applyGuardPolicyPatch(
  current: unknown,
  patch: unknown,
  scope: GuardScope,
): GuardParseResult<StoredGuardPolicy> {
  if (!isPlainObject(patch)) return fail('policy must be an object');
  const extra = unknownKeys(patch, new Set(['signals', 'probe_profiles']));
  if (extra.length > 0) return fail(`policy has unknown field(s): ${extra.join(', ')}`);
  const base = parseGuardPolicyRead(current);
  const signals: Record<string, Record<string, unknown>> = {};
  for (const [kind, section] of Object.entries(base.signals ?? {})) {
    signals[kind] = { ...section };
  }
  if (patch.signals !== undefined) {
    if (!isPlainObject(patch.signals)) return fail('policy.signals must be an object');
    const unknown = Object.keys(patch.signals).filter((key) => !isGuardSignalKind(key)).sort();
    if (unknown.length > 0) return fail(`policy.signals has unknown signal(s): ${unknown.join(', ')}`);
    for (const kind of GUARD_SIGNAL_KINDS) {
      if (!(kind in patch.signals)) continue;
      const raw = patch.signals[kind];
      if (raw === null) {
        delete signals[kind];
        continue;
      }
      if (!isPlainObject(raw)) return fail(`signals.${kind} must be an object or null`);
      const allowed = new Set<string>(guardSignalFields(kind, scope));
      let unset: string[] = [];
      if (UNSET_KEY in raw) {
        const value = raw[UNSET_KEY];
        if (!Array.isArray(value) || !value.every((item) => typeof item === 'string')) {
          return fail(`signals.${kind}.unset must be an array of field names`);
        }
        const bad = value.filter((item) => !allowed.has(item)).sort();
        if (bad.length > 0) return fail(`signals.${kind}.unset has unknown field(s): ${bad.join(', ')}`);
        const both = value.filter((item) => item in raw).sort();
        if (both.length > 0) return fail(`cannot both set and unset signals.${kind}.${both.join(`, signals.${kind}.`)}`);
        unset = [...new Set(value)];
      }
      const next: Record<string, unknown> = { ...(signals[kind] ?? {}) };
      for (const [field, value] of Object.entries(raw)) {
        if (field === UNSET_KEY) continue;
        next[field] = value;
      }
      for (const field of unset) delete next[field];
      if (Object.keys(next).length === 0) delete signals[kind];
      else signals[kind] = next;
    }
  }
  const profiles: Record<string, unknown> = { ...(base.probe_profiles ?? {}) };
  if (patch.probe_profiles !== undefined) {
    if (!isPlainObject(patch.probe_profiles)) return fail('probe_profiles must be an object');
    const unknown = unknownKeys(patch.probe_profiles, new Set<string>(GUARD_PROBE_PROFILES));
    if (unknown.length > 0) return fail(`probe_profiles has unknown profile(s): ${unknown.join(', ')}`);
    for (const profile of GUARD_PROBE_PROFILES) {
      if (!(profile in patch.probe_profiles)) continue;
      const raw = patch.probe_profiles[profile];
      if (raw === null) delete profiles[profile];
      else profiles[profile] = raw;
    }
  }
  const merged: Record<string, unknown> = { v: GUARD_POLICY_VERSION };
  if (Object.keys(signals).length > 0) merged.signals = signals;
  if (Object.keys(profiles).length > 0) merged.probe_profiles = profiles;
  const parsed = parseGuardPolicyWrite(merged, scope);
  if (!parsed.ok || scope !== 'sku') return parsed;
  const touched = GUARD_SIGNAL_KINDS.filter((kind) => {
    if (patch.probe_profiles !== undefined) return true;
    const raw = isPlainObject(patch.signals) ? patch.signals[kind] : undefined;
    return isPlainObject(raw) && ('trip' in raw || 'recover' in raw);
  });
  const missing = missingRecoverProfile(parsed.value.signals ?? {}, parsed.value.probe_profiles, touched);
  return missing === null ? parsed : fail(missing);
}

/** The v1 section keys a PUT body carried before guard-v2. */
export const GUARD_V1_BODY_SECTIONS = ['first_content', 'stall', 'silent_trip', 'silent_recover', 'broken_trip'] as const;

const PUT_BODY_KEYS = new Set(['scope', 'sku_id', 'org_id', 'sku_name', 'policy', 'note']);

/**
 * Envelope of one PUT body (ops API and the gateway write face). A v1 body
 * (`stall`, `silent_trip`, …) is refused by name: the v2 face would otherwise
 * read it as an empty patch and answer 200 having changed nothing, so an old
 * console tab or script would believe it armed a guard. Null = acceptable.
 */
export function checkGuardPutBodyKeys(body: Readonly<Record<string, unknown>>): string | null {
  const v1 = GUARD_V1_BODY_SECTIONS.filter((key) => key in body);
  if (v1.length > 0) {
    return `v1 guard section(s) ${v1.join(', ')} are retired; send the change as policy (guard-v2). Nothing was written.`;
  }
  const extra = Object.keys(body).filter((key) => !PUT_BODY_KEYS.has(key)).sort();
  if (extra.length > 0) return `unknown field(s): ${extra.join(', ')}. Nothing was written.`;
  return null;
}

/**
 * Stateless envelope check of one PUT `policy` patch for a scope: known
 * signals / profiles, fields this scope may write, `unset` an array. The
 * complete check (ranges, required combinations) needs the stored row and
 * runs on the gateway ({@link applyGuardPolicyPatch}). Null = acceptable.
 */
export function checkGuardPolicyPatchShape(patch: unknown, scope: GuardScope): string | null {
  if (!isPlainObject(patch)) return 'policy must be an object';
  const extra = unknownKeys(patch, new Set(['signals', 'probe_profiles']));
  if (extra.length > 0) return `policy has unknown field(s): ${extra.join(', ')}`;
  if (patch.signals !== undefined) {
    if (!isPlainObject(patch.signals)) return 'policy.signals must be an object';
    for (const [kind, raw] of Object.entries(patch.signals)) {
      if (!isGuardSignalKind(kind)) return `policy.signals has unknown signal(s): ${kind}`;
      if (raw === null) continue;
      if (!isPlainObject(raw)) return `signals.${kind} must be an object or null`;
      const allowed = new Set<string>([...guardSignalFields(kind, scope), UNSET_KEY]);
      const bad = unknownKeys(raw, allowed);
      if (bad.length > 0) return `signals.${kind} has field(s) not allowed on a ${scope} row: ${bad.join(', ')}`;
      const unset = raw[UNSET_KEY];
      if (unset !== undefined && (!Array.isArray(unset) || !unset.every((item) => typeof item === 'string'))) {
        return `signals.${kind}.unset must be an array of field names`;
      }
    }
  }
  if (patch.probe_profiles !== undefined) {
    if (scope === 'org_sku') return 'probe_profiles cannot be set on an org×SKU row; recovery is SKU-only';
    if (!isPlainObject(patch.probe_profiles)) return 'probe_profiles must be an object';
    const unknown = unknownKeys(patch.probe_profiles, new Set<string>(GUARD_PROBE_PROFILES));
    if (unknown.length > 0) return `probe_profiles has unknown profile(s): ${unknown.join(', ')}`;
    for (const [profile, raw] of Object.entries(patch.probe_profiles)) {
      if (raw !== null && !isPlainObject(raw)) return `probe_profiles.${profile} must be an object or null`;
    }
  }
  return null;
}

export interface GuardPolicyDiffEntry {
  readonly path: string;
  readonly before: unknown;
  readonly after: unknown;
}

function flattenPolicy(value: unknown, prefix: string, out: Map<string, unknown>): void {
  if (isPlainObject(value)) {
    for (const key of Object.keys(value).sort()) {
      flattenPolicy(value[key], prefix.length > 0 ? `${prefix}.${key}` : key, out);
    }
    return;
  }
  out.set(prefix, value);
}

/**
 * Leaf-level diff of two stored policies (audit: `ops_audit` records the v2
 * diff of each write). Missing on one side is `undefined` there.
 */
export function guardPolicyDiff(before: unknown, after: unknown): readonly GuardPolicyDiffEntry[] {
  const left = new Map<string, unknown>();
  const right = new Map<string, unknown>();
  flattenPolicy(parseGuardPolicyRead(before), '', left);
  flattenPolicy(parseGuardPolicyRead(after), '', right);
  const paths = [...new Set([...left.keys(), ...right.keys()])].sort();
  const out: GuardPolicyDiffEntry[] = [];
  for (const path of paths) {
    const a = left.get(path);
    const b = right.get(path);
    if (JSON.stringify(a) === JSON.stringify(b)) continue;
    out.push({ path, before: a, after: b });
  }
  return out;
}

function readSignalSection(kind: GuardSignalKind, value: unknown): GuardSignalSection | undefined {
  if (!isPlainObject(value)) return undefined;
  const spec = guardSignalSpec(kind);
  const out: {
    threshold_ms?: number | null;
    walk?: GuardMode | null;
    retry_after_reasoning?: boolean | null;
    retry_after_cap_s?: number | null;
    chat_lane?: boolean | null;
    trip?: GuardTripSpec | null;
    recover?: GuardRecoverSpec | null;
    global_trip?: GuardGlobalTripChoice | null;
    borrow_global?: boolean | null;
  } = {};
  if ('threshold_ms' in value && spec.threshold !== null) {
    if (value.threshold_ms === null) out.threshold_ms = null;
    else if (isIntInRange(value.threshold_ms, spec.threshold.min, spec.threshold.max)) out.threshold_ms = value.threshold_ms;
  }
  if ('walk' in value && spec.walk !== 'none') {
    if (value.walk === null) out.walk = null;
    else if (isGuardMode(value.walk)) out.walk = value.walk;
  }
  if ('retry_after_reasoning' in value && spec.retryAfterReasoning) {
    if (value.retry_after_reasoning === null) out.retry_after_reasoning = null;
    else if (typeof value.retry_after_reasoning === 'boolean') out.retry_after_reasoning = value.retry_after_reasoning;
  }
  if ('retry_after_cap_s' in value && spec.retryAfterCap) {
    if (value.retry_after_cap_s === null) out.retry_after_cap_s = null;
    else if (isIntInRange(value.retry_after_cap_s, GUARD_RETRY_AFTER_CAP_SECONDS_MIN, GUARD_RETRY_AFTER_CAP_SECONDS_MAX)) {
      out.retry_after_cap_s = value.retry_after_cap_s;
    }
  }
  if ('chat_lane' in value && spec.chatLane) {
    if (value.chat_lane === null) out.chat_lane = null;
    else if (typeof value.chat_lane === 'boolean') out.chat_lane = value.chat_lane;
  }
  if ('trip' in value) {
    if (value.trip === null) out.trip = null;
    else {
      const parsed = parseTrip(value.trip, 'trip');
      if (parsed.ok) out.trip = parsed.value;
    }
  }
  if ('recover' in value) {
    if (value.recover === null) out.recover = null;
    else {
      const parsed = parseRecover(value.recover, 'recover');
      if (parsed.ok) out.recover = parsed.value;
    }
  }
  if ('global_trip' in value) {
    if (value.global_trip === null) out.global_trip = null;
    else if (value.global_trip === 'inherit' || value.global_trip === 'own') out.global_trip = value.global_trip;
  }
  if ('borrow_global' in value) {
    if (value.borrow_global === null) out.borrow_global = null;
    else if (typeof value.borrow_global === 'boolean') out.borrow_global = value.borrow_global;
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

/**
 * Read parser. Not a policy we understand → {@link EMPTY_GUARD_POLICY}
 * (all off). Illegal fields inside a known section are dropped; JSON null is
 * kept as explicit off.
 */
export function parseGuardPolicyRead(input: unknown): StoredGuardPolicy {
  const raw: unknown = typeof input === 'string' ? safeJsonParse(input) : input;
  if (!isPlainObject(raw)) return EMPTY_GUARD_POLICY;
  if (unknownKeys(raw, TOP_LEVEL_KEYS).length > 0) return EMPTY_GUARD_POLICY;
  if (raw.v !== GUARD_POLICY_VERSION) return EMPTY_GUARD_POLICY;
  const signals: Partial<Record<GuardSignalKind, GuardSignalSection>> = {};
  if (raw.signals !== undefined) {
    if (!isPlainObject(raw.signals)) return EMPTY_GUARD_POLICY;
    if (Object.keys(raw.signals).some((key) => !isGuardSignalKind(key))) return EMPTY_GUARD_POLICY;
    for (const kind of GUARD_SIGNAL_KINDS) {
      if (!(kind in raw.signals)) continue;
      const section = readSignalSection(kind, raw.signals[kind]);
      if (section !== undefined) signals[kind] = section;
    }
  }
  const profiles: { short?: GuardShortProfileSpec; long_stream?: GuardLongStreamProfileSpec } = {};
  if (raw.probe_profiles !== undefined) {
    if (!isPlainObject(raw.probe_profiles)) return EMPTY_GUARD_POLICY;
    const short = parseShortProfile(raw.probe_profiles.short, 'short');
    if (short.ok) profiles.short = short.value;
    const long = parseLongProfile(raw.probe_profiles.long_stream, 'long_stream');
    if (long.ok) profiles.long_stream = long.value;
  }
  return {
    v: 2,
    ...(Object.keys(signals).length > 0 ? { signals } : {}),
    ...(Object.keys(profiles).length > 0 ? { probe_profiles: profiles } : {}),
  };
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export interface ResolvedGuardTrip {
  readonly mode: 'shadow' | 'act';
  readonly window_s: number;
  readonly min_hits: number;
  readonly min_ratio: number;
  readonly max_hold_s: number;
}

/** The org×SKU layer of one signal; present only when an org row decides something. */
export interface ResolvedGuardOrgSignal {
  readonly org_id: string;
  /** The org's own trip (org-scoped fault rows, only this org's routing). */
  readonly trip?: ResolvedGuardTrip;
  /** `global_trip: own`: SKU-global faults of this signal do not retire legs for this org. */
  readonly own?: true;
  /**
   * Add SKU-global hits to the org decision (default on). Only when a hit at
   * the SKU threshold is also a hit at the org's: thresholdless signals, or
   * an org threshold no wider than the SKU's.
   */
  readonly borrow?: true;
}

/**
 * One signal as this request sees it. Absent keys are off. Never carries a
 * JSON null, so a resolved policy is a plain cacheable value.
 */
export interface ResolvedGuardSignal {
  /** This request's disposal (org-merged). */
  readonly walk?: 'shadow' | 'act';
  /** This request's threshold (org-merged). */
  readonly threshold_ms?: number;
  readonly retry_after_reasoning?: true;
  readonly retry_after_cap_s?: number;
  /**
   * S4: the act walk also arms on the chat / completions lane. Present only
   * when the merged `chat_lane` is true and the merged walk is `act`.
   */
  readonly chat_lane?: true;
  /** SKU-global trip window and decision. */
  readonly trip?: ResolvedGuardTrip;
  /** SKU recovery: required profile and clean waves. */
  readonly recover?: GuardRecoverSpec;
  /**
   * This request's threshold or walk differs from the SKU's: its evidence
   * for this signal must not enter the SKU-global window (02-design §12.3).
   */
  readonly censored?: true;
  readonly org?: ResolvedGuardOrgSignal;
}

export interface ResolvedGuardPolicy {
  readonly signals: Readonly<Partial<Record<GuardSignalKind, ResolvedGuardSignal>>>;
  readonly probe_profiles?: GuardProbeProfilesSection;
}

function hasField(section: object | undefined, field: string): boolean {
  return section !== undefined && field in section;
}

function overlay<T>(
  orgHas: boolean,
  orgValue: T | null | undefined,
  skuHas: boolean,
  skuValue: T | null | undefined,
): T | undefined {
  if (orgHas) return orgValue === null || orgValue === undefined ? undefined : orgValue;
  if (skuHas) return skuValue === null || skuValue === undefined ? undefined : skuValue;
  return undefined;
}

function armedMode(mode: GuardMode | null | undefined): 'shadow' | 'act' | undefined {
  return mode === 'shadow' || mode === 'act' ? mode : undefined;
}

function armedTrip(trip: GuardTripSpec | null | undefined): ResolvedGuardTrip | undefined {
  if (trip === null || trip === undefined || trip.mode === 'off') return undefined;
  return {
    mode: trip.mode,
    window_s: trip.window_s,
    min_hits: trip.min_hits,
    min_ratio: trip.min_ratio,
    max_hold_s: trip.max_hold_s,
  };
}

/** walk + threshold as the request path uses them: a threshold signal is armed only with both. */
function effectiveDisposal(
  spec: GuardSignalSpec,
  walk: GuardMode | undefined,
  threshold: number | undefined,
): { readonly walk?: 'shadow' | 'act'; readonly threshold?: number } {
  const armed = armedMode(walk);
  if (spec.threshold === null) return armed !== undefined ? { walk: armed } : {};
  if (armed !== undefined && threshold !== undefined) return { walk: armed, threshold };
  return threshold !== undefined ? { threshold } : {};
}

function resolveSignal(
  kind: GuardSignalKind,
  sku: GuardSignalSection | undefined,
  org: GuardSignalSection | undefined,
  orgId: string | undefined,
): ResolvedGuardSignal | undefined {
  const spec = guardSignalSpec(kind);
  const skuWalk = overlay(false, undefined, hasField(sku, 'walk'), sku?.walk);
  const skuThreshold = overlay(false, undefined, hasField(sku, 'threshold_ms'), sku?.threshold_ms);
  const walk = overlay(hasField(org, 'walk'), org?.walk, hasField(sku, 'walk'), sku?.walk);
  const threshold = overlay(hasField(org, 'threshold_ms'), org?.threshold_ms, hasField(sku, 'threshold_ms'), sku?.threshold_ms);
  const skuDisposal = effectiveDisposal(spec, skuWalk, skuThreshold);
  const disposal = effectiveDisposal(spec, walk, threshold);
  const retry = spec.retryAfterReasoning
    ? overlay(hasField(org, 'retry_after_reasoning'), org?.retry_after_reasoning, hasField(sku, 'retry_after_reasoning'), sku?.retry_after_reasoning)
    : undefined;
  const cap = spec.retryAfterCap ? overlay(false, undefined, hasField(sku, 'retry_after_cap_s'), sku?.retry_after_cap_s) : undefined;
  // Not part of the disposal comparison below: chat-lane walks never feed a
  // trip window (02-design addendum, CHAT-SERVER-ERROR-WALK C6/C8).
  const chatLane = spec.chatLane
    ? overlay(hasField(org, 'chat_lane'), org?.chat_lane, hasField(sku, 'chat_lane'), sku?.chat_lane)
    : undefined;
  const trip = armedTrip(sku?.trip);
  const recover = sku?.recover ?? undefined;
  // An org whose disposal (walk or threshold) differs from the SKU's is not
  // sampling what the SKU-global window measures (R1-B8: a walk-only signal
  // too — an org that turns S1 / S4 on would otherwise feed the global
  // window alone and trip the leg for every org).
  // The flag only matters to a SKU trip's window; a walk-only signal without
  // one carries no flag (its resolved shape is unchanged).
  const censored = (skuDisposal.walk !== disposal.walk || skuDisposal.threshold !== disposal.threshold)
    && (spec.threshold !== null || trip !== undefined);
  let orgLayer: ResolvedGuardOrgSignal | undefined;
  if (org !== undefined && orgId !== undefined) {
    const orgTrip = armedTrip(org.trip);
    const own = org.global_trip === 'own';
    const narrower = spec.threshold === null
      || skuDisposal.threshold === undefined
      || (disposal.threshold !== undefined && disposal.threshold <= skuDisposal.threshold);
    const borrow = org.borrow_global !== false && narrower;
    if (orgTrip !== undefined || own) {
      orgLayer = {
        org_id: orgId,
        ...(orgTrip !== undefined ? { trip: orgTrip } : {}),
        ...(own ? { own: true as const } : {}),
        ...(orgTrip !== undefined && borrow ? { borrow: true as const } : {}),
      };
    }
  }
  const walkOut = kind === 'stall'
    ? (disposal.walk !== undefined && disposal.threshold !== undefined ? disposal.walk : undefined)
    : disposal.walk;
  const out: ResolvedGuardSignal = {
    ...(walkOut !== undefined ? { walk: walkOut } : {}),
    ...(disposal.threshold !== undefined && (kind !== 'stall' || walkOut !== undefined)
      ? { threshold_ms: disposal.threshold }
      : {}),
    ...(kind === 'stall' && walkOut !== undefined && retry === true ? { retry_after_reasoning: true as const } : {}),
    ...(cap !== undefined ? { retry_after_cap_s: cap } : {}),
    ...(walkOut === 'act' && chatLane === true ? { chat_lane: true as const } : {}),
    ...(trip !== undefined ? { trip } : {}),
    ...((trip !== undefined || orgLayer?.trip !== undefined) && recover !== undefined ? { recover } : {}),
    ...(censored ? { censored: true as const } : {}),
    ...(orgLayer !== undefined ? { org: orgLayer } : {}),
  };
  const meaningful = out.walk !== undefined || out.threshold_ms !== undefined || out.trip !== undefined
    || out.org !== undefined || out.retry_after_cap_s !== undefined;
  return meaningful ? out : undefined;
}

/**
 * Pure merge of a SKU row and (optionally) the request org's row. Returns
 * null when every signal is off, so an all-off SKU carries no key on the
 * routing result or the candidate-cache blob. The legacy cutoff column is not
 * folded in (see {@link resolveNoResponseCutoffMs}).
 */
export function resolveGuardPolicy(params: {
  readonly sku?: unknown;
  readonly orgSku?: unknown;
  readonly orgId?: string;
}): ResolvedGuardPolicy | null {
  const sku = parseGuardPolicyRead(params.sku);
  const org = parseGuardPolicyRead(params.orgSku);
  const signals: Partial<Record<GuardSignalKind, ResolvedGuardSignal>> = {};
  for (const kind of GUARD_SIGNAL_KINDS) {
    const resolved = resolveSignal(kind, sku.signals?.[kind], org.signals?.[kind], params.orgId);
    if (resolved !== undefined) signals[kind] = resolved;
  }
  if (Object.keys(signals).length === 0) return null;
  const anyTrip = Object.values(signals).some((signal) => signal.trip !== undefined || signal.org?.trip !== undefined);
  return {
    signals,
    ...(anyTrip && sku.probe_profiles !== undefined ? { probe_profiles: sku.probe_profiles } : {}),
  };
}

/**
 * Effective stream first-content cutoff for forwarding (#2744 merge rule,
 * unchanged by v2): when the new table says anything about the stream
 * first-content cut on the org or SKU row (a `threshold_ms` or `walk` key,
 * including JSON null), the table decides — the cut is armed only with walk
 * `act` and a threshold; otherwise the legacy SKU column applies.
 */
export function resolveNoResponseCutoffMs(params: {
  readonly sku?: unknown;
  readonly orgSku?: unknown;
  readonly legacyStreamCutoffMs?: number | null;
}): number | null {
  const sku = parseGuardPolicyRead(params.sku).signals?.first_content_stream;
  const org = parseGuardPolicyRead(params.orgSku).signals?.first_content_stream;
  const tableDecides = hasField(org, 'threshold_ms') || hasField(org, 'walk')
    || hasField(sku, 'threshold_ms') || hasField(sku, 'walk');
  if (tableDecides) {
    const walk = overlay(hasField(org, 'walk'), org?.walk, hasField(sku, 'walk'), sku?.walk);
    const threshold = overlay(hasField(org, 'threshold_ms'), org?.threshold_ms, hasField(sku, 'threshold_ms'), sku?.threshold_ms);
    return walk === 'act' && threshold !== undefined ? threshold : null;
  }
  const legacy = params.legacyStreamCutoffMs;
  return legacy !== null && legacy !== undefined
    && isIntInRange(legacy, GUARD_FIRST_CONTENT_STREAM_MS_MIN, GUARD_FIRST_CONTENT_STREAM_MS_MAX)
    ? legacy
    : null;
}

/** Signals whose SKU-global trip is `act` (selector load gate). */
export function guardActTripKinds(policy: ResolvedGuardPolicy | null | undefined): readonly GuardSignalKind[] {
  if (policy === null || policy === undefined) return [];
  return GUARD_SIGNAL_KINDS.filter((kind) => policy.signals[kind]?.trip?.mode === 'act');
}
